import { useState, useEffect } from "react";
import { Form, Button } from "react-bootstrap";
import {
  MdLocalHospital, MdEdit, MdAdd, MdCalendarToday, MdHotel, MdMedicalServices,
  MdWorkspacePremium, MdLocationOn, MdPictureAsPdf, MdPlayArrow, MdOpenInNew, MdArticle,
  MdVideocam, MdSchool,
} from "react-icons/md";
import {
  FaGlobe, FaFacebookF, FaTwitter, FaInstagram, FaLinkedinIn, FaYoutube,
} from "react-icons/fa";
import { AwardCard } from "../../../../Components/ui/AwardCard/AwardCard";
import { ArticleCard } from "../../../../Components/ui/ArticleCard/ArticleCard";
import { VideoCard } from "../../../../Components/ui/VideoCard/VideoCard";
import { UPLOADS_BASE } from "../../constants/api";
import { mapArticleToCardProps } from "../../utils/newsArticleHelpers";
import { mapCmeToVideoCardProps } from "../../utils/digitalCmeHelpers";
import {
  getYoutubeThumbnailUrl,
  getYoutubeEmbedUrl,
  getYoutubeWatchUrl,
} from "../../utils/youtubeHelpers";

const HM_GALLERY_PLACEHOLDERS = [
  { id: 1, color: "#e8f4fc" },
  { id: 2, color: "#eef2ff" },
  { id: 3, color: "#f0fdf4" },
];

// UI preview data — replace with API response when backend is ready
const HM_DEMO_HOSPITAL = {
  establishment_id: "demo",
  establishment_name: "Amrita Hospital",
  parent_establishment_name: "Amrita Hospital South India",
  category: "Multi Specialty",
  establishment_year: 2010,
  accreditation_display: "JCI, NABH",
  short_profile:
    "An amazing hospital in Delhi NCR, Amrita Hospital is a leading multi-specialty healthcare institution providing world-class medical care with state-of-the-art facilities.",
  long_profile:
    "Amrita Hospital is a leading multi-specialty healthcare institution providing world-class medical care with state-of-the-art facilities and compassionate patient services. Our hospital is equipped with advanced diagnostic and treatment technologies, staffed by experienced medical professionals dedicated to delivering excellence in patient care. We offer a comprehensive range of specialties including cardiology, oncology, neurology, orthopedics, and emergency services.",
  address: "Sector 62, Faridabad",
  city_name: "Faridabad",
  state_name: "Haryana",
  country_name: "India",
  total_beds: 1200,
  total_doctors: 400,
  website_address: "https://www.amritahospital.org",
  facebook_page: "https://www.facebook.com",
  x_profile: "https://www.twitter.com",
  instagram: "https://www.instagram.com",
  linkedin: "https://www.linkedin.com",
  youtube_channel: "https://www.youtube.com",
  virtual_tour_url: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
  profile_pdf_available: true,
};

const getAccreditationLabels = (ids, accreditations) => {
  if (!ids) return "—";
  return ids
    .toString()
    .split(",")
    .map((id) => accreditations.find((a) => String(a.accreditation_id) === id.trim())?.accreditation_title)
    .filter(Boolean)
    .join(", ") || "—";
};

const HospitalManagementView = ({
  establishments,
  accreditations,
  mediaGallery = [],
  awardsRecognition = [],
  newsArticles = [],
  digitalCme = [],
  onEdit,
  showCreateProfile,
  onCreateProfile,
}) => {
  const isPreview = establishments.length === 0;
  const hospitalList = isPreview ? [HM_DEMO_HOSPITAL] : establishments;
  const [selectedId, setSelectedId] = useState("");
  const [virtualTourPlaying, setVirtualTourPlaying] = useState(false);

  useEffect(() => {
    if (hospitalList.length > 0 && !selectedId) {
      setSelectedId(String(hospitalList[0].establishment_id));
    }
  }, [hospitalList, selectedId]);

  useEffect(() => {
    setVirtualTourPlaying(false);
  }, [selectedId]);

  const hospital = hospitalList.find((e) => String(e.establishment_id) === String(selectedId));

  if (!hospital) return null;

  const accrLabel = hospital.accreditation_display
    || getAccreditationLabels(hospital.accreditation_ids, accreditations);
  const addressParts = [hospital.address, hospital.city_name, hospital.state_name, hospital.country_name]
    .filter(Boolean)
    .join(", ");
  const logoUrl = hospital.logo_path ? `${UPLOADS_BASE}/uploads/${hospital.logo_path}` : null;
  const pdfUrl = hospital.profile_pdf_path
    ? `${UPLOADS_BASE}/uploads/${hospital.profile_pdf_path}`
    : null;
  const showPdfBtn = pdfUrl || hospital.profile_pdf_available;

  const connectLinks = [
    { key: "website", label: "Website", icon: FaGlobe, url: hospital.website_address },
    { key: "facebook", label: "Facebook", icon: FaFacebookF, url: hospital.facebook_page },
    { key: "twitter", label: "Twitter", icon: FaTwitter, url: hospital.x_profile },
    { key: "instagram", label: "Instagram", icon: FaInstagram, url: hospital.instagram },
    { key: "linkedin", label: "LinkedIn", icon: FaLinkedinIn, url: hospital.linkedin },
    { key: "youtube", label: "YouTube", icon: FaYoutube, url: hospital.youtube_channel },
  ].filter((l) => l.url && !String(l.url).includes("/embed/"));

  const stats = [
    { id: "beds", label: "Beds", value: hospital.total_beds ?? "—", icon: MdHotel },
    { id: "doctors", label: "Doctors", value: hospital.total_doctors ?? "—", icon: MdMedicalServices },
    { id: "accr", label: "Accreditation", value: accrLabel, icon: MdWorkspacePremium },
  ];

  const virtualTourUrl = hospital.virtual_tour_url || hospital.youtube_channel;
  const virtualTourThumbnail = getYoutubeThumbnailUrl(virtualTourUrl);
  const virtualTourEmbedUrl = getYoutubeEmbedUrl(virtualTourUrl);
  const virtualTourWatchUrl = getYoutubeWatchUrl(virtualTourUrl);

  const galleryImages = mediaGallery.filter((m) => m.media_type === "Image");
  const galleryVideos = mediaGallery.filter((m) => m.media_type === "Video");

  const mapAwardToCard = (award) => {
    const images = (award.images || []).map(
      (img) => `${UPLOADS_BASE}/uploads/${img.image_path}`
    );
    return {
      id: award.award_id,
      images,
      title: award.award_title,
      organization: award.awarding_body || "",
      description: award.short_note || "",
      certificateUrl: images[0] || "#",
    };
  };

  const addressDisplay = hospital.city_name || addressParts || "—";

  return (
    <div className="hm-page">
      <div className="hm-topbar">
        <div className="hm-brand">
          <MdLocalHospital className="hm-brand-icon" />
          <span>Hospital Management</span>
        </div>
        <div className="hm-topbar-actions">
          {hospitalList.length > 1 && (
            <Form.Select
              className="hm-select"
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
            >
              {hospitalList.map((est) => (
                <option key={est.establishment_id} value={est.establishment_id}>
                  {est.establishment_name}
                </option>
              ))}
            </Form.Select>
          )}
          {onEdit && (
            <Button type="button" className="hm-edit-btn" onClick={onEdit}>
              <MdEdit /> Edit
            </Button>
          )}
          {showCreateProfile && (
            <Button type="button" className="hm-create-profile-btn" onClick={onCreateProfile}>
              <MdAdd /> Create/ Edit Profile
            </Button>
          )}
        </div>
      </div>

      <div className="hm-hero">
        <div className="hm-hero-logo">
          {logoUrl ? (
            <img src={logoUrl} alt={`${hospital.establishment_name} logo`} />
          ) : (
            <div className="hm-hero-logo-placeholder">
              <MdLocalHospital size={44} />
              <span className="hm-hero-shield" aria-hidden="true" />
            </div>
          )}
        </div>
        <div className="hm-hero-content">
          <div className="hm-badges">
            {hospital.category && <span className="hm-badge">{hospital.category}</span>}
            {hospital.establishment_year && (
              <span className="hm-badge">
                <MdCalendarToday size={13} /> Est. {hospital.establishment_year}
              </span>
            )}
            {accrLabel !== "—" && (
              <span className="hm-badge">
                <MdWorkspacePremium size={13} /> {accrLabel}
              </span>
            )}
          </div>
          <h1 className="hm-hero-title">{hospital.establishment_name}</h1>
          {hospital.parent_establishment_name && (
            <p className="hm-hero-subtitle">A unit of {hospital.parent_establishment_name}</p>
          )}
          {hospital.short_profile && (
            <p className="hm-hero-desc">{hospital.short_profile}</p>
          )}
        </div>
      </div>

      <div className="hm-stats-row">
        {stats.map(({ id, label, value, icon: Icon }) => (
          <div key={id} className="hm-stat-card">
            <div className="hm-stat-icon">
              <Icon />
            </div>
            <div className="hm-stat-text">
              <span className="hm-stat-label">{label}</span>
              <span className={`hm-stat-value${id === "accr" ? " hm-stat-value--sm" : ""}`}>{value}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="hm-about-grid">
        <div className="hm-card hm-card--about">
          <h3 className="hm-card-title">About</h3>
          <p className="hm-about-text">
            {hospital.long_profile || hospital.short_profile || "No description available."}
          </p>
        </div>
        <div className="hm-sidebar-cards">
          <div className="hm-card hm-card--sidebar">
            <h3 className="hm-card-title">
              <MdLocationOn /> Address
            </h3>
            <p className="hm-card-text">{addressDisplay}</p>
          </div>
          <div className="hm-card hm-card--sidebar">
            <h3 className="hm-card-title">
              <MdPictureAsPdf /> PDF Profile
            </h3>
            {showPdfBtn ? (
              pdfUrl ? (
                <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="hm-download-btn">
                  <MdPictureAsPdf /> Download PDF
                </a>
              ) : (
                <button type="button" className="hm-download-btn" disabled>
                  <MdPictureAsPdf /> Download PDF
                </button>
              )
            ) : (
              <p className="hm-card-text hm-card-text--muted">No PDF profile uploaded.</p>
            )}
          </div>
        </div>
      </div>

      <div className="hm-media-grid">
        <div className="hm-card hm-card--media">
          <h3 className="hm-card-title">
            <MdPlayArrow /> Virtual Tour
          </h3>
          {virtualTourThumbnail ? (
            virtualTourPlaying && virtualTourEmbedUrl ? (
              <div className="hm-video-wrap">
                <iframe
                  title="Virtual Tour"
                  src={`${virtualTourEmbedUrl}?autoplay=1`}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            ) : (
              <div className="hm-video-thumb-wrap">
                <button
                  type="button"
                  className="hm-video-thumb"
                  onClick={() => setVirtualTourPlaying(true)}
                  aria-label="Play virtual tour"
                >
                  <img src={virtualTourThumbnail} alt="Virtual tour preview" />
                  <span className="hm-video-thumb-play">
                    <MdPlayArrow size={48} />
                  </span>
                </button>
                {virtualTourWatchUrl && (
                  <a
                    href={virtualTourWatchUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hm-video-thumb-external"
                    aria-label="Open on YouTube"
                  >
                    <MdOpenInNew size={18} />
                  </a>
                )}
              </div>
            )
          ) : (
            <div className="hm-video-placeholder">
              <MdPlayArrow size={40} />
              <span>No virtual tour video available</span>
            </div>
          )}
        </div>

        <div className="hm-card hm-card--media">
          <h3 className="hm-card-title">Connect</h3>
          {connectLinks.length > 0 ? (
            <div className="hm-connect-grid">
              {connectLinks.map(({ key, label, icon: Icon, url }) => (
                <a key={key} href={url} target="_blank" rel="noopener noreferrer" className="hm-connect-btn">
                  <Icon /> {label}
                </a>
              ))}
            </div>
          ) : (
            <p className="hm-card-text hm-card-text--muted">No social links added yet.</p>
          )}
        </div>
      </div>

      <div className="hm-card">
        <h3 className="hm-card-title">Picture Gallery</h3>
        <div className="hm-gallery">
          {galleryImages.map((item) => (
            <div key={item.media_id} className="hm-gallery-item">
              <img
                src={`${UPLOADS_BASE}/uploads/${item.media_upload}`}
                alt={item.short_note || "Gallery image"}
              />
            </div>
          ))}
          {!isPreview && galleryImages.length === 0 && (
            <p className="hm-card-text hm-card-text--muted hm-gallery-empty">
              No pictures uploaded yet. Add images from Picture/ Video Gallery.
            </p>
          )}
          {isPreview && galleryImages.length === 0 && HM_GALLERY_PLACEHOLDERS.map((item) => (
            <div key={item.id} className="hm-gallery-item hm-gallery-item--placeholder" style={{ background: item.color }}>
              <MdLocalHospital size={40} color="#0f66a4" />
            </div>
          ))}
        </div>
      </div>

      <div className="hm-card">
        <h3 className="hm-card-title">
          <MdVideocam /> Video Gallery
        </h3>
        {galleryVideos.length > 0 ? (
          <div className="hm-gallery hm-gallery--videos">
            {galleryVideos.map((item) => {
              const thumb = getYoutubeThumbnailUrl(item.media_link);
              const watchUrl = getYoutubeWatchUrl(item.media_link);
              return (
                <div key={item.media_id} className="hm-gallery-item media-gallery-item media-gallery-item--video">
                  {thumb ? (
                    <a href={watchUrl || item.media_link} target="_blank" rel="noopener noreferrer">
                      <img src={thumb} alt={item.short_note || "Video"} />
                      <span className="media-gallery-item__play">
                        <MdPlayArrow size={28} />
                      </span>
                    </a>
                  ) : (
                    <div className="media-gallery-item__link-fallback">
                      <MdPlayArrow size={28} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <p className="hm-card-text hm-card-text--muted">
            No videos added yet. Add video links from Picture/ Video Gallery.
          </p>
        )}
      </div>

      <div className="hm-card">
        <h3 className="hm-card-title">
          <MdWorkspacePremium /> Awards &amp; Recognition
        </h3>
        {awardsRecognition.length > 0 ? (
          <div className="hm-awards-scroll">
            <div className="hm-awards-scroll__track">
              {awardsRecognition.map((award) => (
                <div key={award.award_id} className="hm-awards-scroll__item">
                  <AwardCard {...mapAwardToCard(award)} />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <p className="hm-card-text hm-card-text--muted">
            No awards added yet. Add awards from Awards &amp; Recognitions menu.
          </p>
        )}
      </div>

      <div className="hm-card">
        <h3 className="hm-card-title">
          <MdArticle /> News &amp; Articles
        </h3>
        {newsArticles.length > 0 ? (
          <div className="hm-articles-scroll">
            <div className="hm-articles-scroll__track">
              {newsArticles.map((article) => (
                <div key={article.news_article_id} className="hm-articles-scroll__item">
                  <ArticleCard {...mapArticleToCardProps(article)} />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <p className="hm-card-text hm-card-text--muted">
            No news articles added yet. Add articles from News &amp; Articles menu.
          </p>
        )}
      </div>

      <div className="hm-card">
        <h3 className="hm-card-title">
          <MdSchool /> Digital CME
        </h3>
        {digitalCme.length > 0 ? (
          <div className="hm-cme-scroll">
            <div className="hm-cme-scroll__track">
              {digitalCme.map((item) => (
                <div key={item.digital_cme_id} className="hm-cme-scroll__item">
                  <VideoCard {...mapCmeToVideoCardProps(item)} />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <p className="hm-card-text hm-card-text--muted">
            No digital CME added yet. Add sessions from Digital CME menu.
          </p>
        )}
      </div>
    </div>
  );
};

export default HospitalManagementView;
